/**
 * Security Check Module
 * Detects security vulnerabilities (OWASP Top 10)
 */

import type { CheckContext, CodeReviewFinding } from '../types.js';
import { generateFindingId } from '../types.js';

// Patterns for detecting hardcoded secrets
const SECRET_PATTERNS = [
  { pattern: /["']sk[-_]live[-_][a-zA-Z0-9]{20,}["']/gi, name: 'Stripe Live Key' },
  { pattern: /["']sk[-_]test[-_][a-zA-Z0-9]{20,}["']/gi, name: 'Stripe Test Key' },
  { pattern: /["'][a-zA-Z0-9]{40}["']/g, name: 'Generic API Key (40 chars)' },
  { pattern: /password\s*[=:]\s*["'][^"']{4,}["']/gi, name: 'Hardcoded Password' },
  { pattern: /apikey\s*[=:]\s*["'][^"']{8,}["']/gi, name: 'Hardcoded API Key' },
  { pattern: /secret\s*[=:]\s*["'][^"']{8,}["']/gi, name: 'Hardcoded Secret' },
  { pattern: /connectionstring\s*[=:]\s*["'][^"']+["']/gi, name: 'Connection String' },
  { pattern: /Bearer\s+[a-zA-Z0-9\-_.]+/gi, name: 'Bearer Token' },
  { pattern: /-----BEGIN\s+(RSA\s+)?PRIVATE\s+KEY-----/gi, name: 'Private Key' },
];

// SQL Injection patterns
const SQL_INJECTION_PATTERNS = [
  { pattern: /\$["'`]SELECT\s+.+\s+FROM/gi, name: 'String interpolation in SQL' },
  { pattern: /\+\s*["'].*SELECT.*FROM/gi, name: 'Concatenation in SQL' },
  { pattern: /string\.Format\s*\(\s*["'].*SELECT/gi, name: 'String.Format with SQL' },
  { pattern: /ExecuteSqlRaw\s*\(\s*\$"/gi, name: 'ExecuteSqlRaw with interpolation' },
  { pattern: /FromSqlRaw\s*\(\s*\$"/gi, name: 'FromSqlRaw with interpolation' },
];

// XSS patterns
const XSS_PATTERNS = [
  { pattern: /innerHTML\s*=/gi, name: 'innerHTML assignment' },
  { pattern: /dangerouslySetInnerHTML/gi, name: 'dangerouslySetInnerHTML' },
  { pattern: /document\.write\s*\(/gi, name: 'document.write' },
  { pattern: /eval\s*\(/gi, name: 'eval() usage' },
];

// Authorization patterns (missing [Authorize])
const CONTROLLER_WITHOUT_AUTH = /\[ApiController\][\s\S]*?public\s+class\s+\w+Controller/gi;

export async function checkSecurity(context: CheckContext): Promise<CodeReviewFinding[]> {
  const findings: CodeReviewFinding[] = [];

  for (const file of context.files) {
    // Skip non-code files
    if (!['csharp', 'typescript', 'javascript', 'tsx', 'jsx'].includes(file.language)) {
      continue;
    }

    const lines = file.content.split('\n');

    // Check for hardcoded secrets
    for (const { pattern, name } of SECRET_PATTERNS) {
      let match;
      pattern.lastIndex = 0;
      while ((match = pattern.exec(file.content)) !== null) {
        const lineNumber = getLineNumber(file.content, match.index);
        const lineContent = lines[lineNumber - 1]?.trim() || '';

        // Skip if in comments
        if (isInComment(lineContent)) continue;

        findings.push({
          id: generateFindingId('security'),
          category: 'security',
          severity: 'blocking',
          title: `Hardcoded ${name}`,
          description: `Found a hardcoded ${name.toLowerCase()} in the source code. Secrets should never be committed to version control.`,
          file: file.relativePath,
          line: lineNumber,
          code: truncateCode(lineContent),
          suggestion: 'Move this secret to environment variables, Azure Key Vault, or a secure configuration provider.',
          autoFixable: false,
          cweId: 'CWE-798',
          references: ['https://cwe.mitre.org/data/definitions/798.html'],
        });
      }
    }

    // Check for SQL Injection (C# only)
    if (file.language === 'csharp') {
      for (const { pattern, name } of SQL_INJECTION_PATTERNS) {
        let match;
        pattern.lastIndex = 0;
        while ((match = pattern.exec(file.content)) !== null) {
          const lineNumber = getLineNumber(file.content, match.index);
          const lineContent = lines[lineNumber - 1]?.trim() || '';

          if (isInComment(lineContent)) continue;

          findings.push({
            id: generateFindingId('security'),
            category: 'security',
            severity: 'blocking',
            title: 'Potential SQL Injection',
            description: `${name} detected. This pattern can lead to SQL injection vulnerabilities.`,
            file: file.relativePath,
            line: lineNumber,
            code: truncateCode(lineContent),
            suggestion: 'Use parameterized queries or EF Core LINQ methods instead of string interpolation.',
            autoFixable: false,
            cweId: 'CWE-89',
            references: ['https://cwe.mitre.org/data/definitions/89.html'],
          });
        }
      }

      // Check for missing [Authorize] on controllers
      const controllerMatch = CONTROLLER_WITHOUT_AUTH.exec(file.content);
      if (controllerMatch) {
        const hasAuthorize = /\[Authorize\]/i.test(file.content.slice(0, controllerMatch.index + 200));
        const hasAllowAnonymous = /\[AllowAnonymous\]/i.test(file.content.slice(0, controllerMatch.index + 200));
        const hasNavRoute = /\[NavRoute\(/i.test(file.content);

        if (!hasAuthorize && !hasAllowAnonymous && !hasNavRoute) {
          findings.push({
            id: generateFindingId('security'),
            category: 'security',
            severity: 'critical',
            title: 'Missing Authorization Attribute',
            description: 'This API controller does not have [Authorize], [AllowAnonymous], or [NavRoute] attribute.',
            file: file.relativePath,
            line: getLineNumber(file.content, controllerMatch.index),
            suggestion: 'Add [Authorize] or [NavRoute("...")] attribute to secure the controller.',
            autoFixable: false,
            cweId: 'CWE-862',
          });
        }
      }
    }

    // Check for XSS (TypeScript/JavaScript/TSX/JSX)
    if (['typescript', 'javascript', 'tsx', 'jsx'].includes(file.language)) {
      for (const { pattern, name } of XSS_PATTERNS) {
        let match;
        pattern.lastIndex = 0;
        while ((match = pattern.exec(file.content)) !== null) {
          const lineNumber = getLineNumber(file.content, match.index);
          const lineContent = lines[lineNumber - 1]?.trim() || '';

          if (isInComment(lineContent)) continue;

          // Skip if sanitization is nearby
          const contextStart = Math.max(0, match.index - 200);
          const contextEnd = Math.min(file.content.length, match.index + 200);
          const context = file.content.slice(contextStart, contextEnd).toLowerCase();
          if (context.includes('sanitize') || context.includes('dompurify') || context.includes('escape')) {
            continue;
          }

          findings.push({
            id: generateFindingId('security'),
            category: 'security',
            severity: 'critical',
            title: 'Potential XSS Vulnerability',
            description: `${name} detected without apparent sanitization. This can lead to Cross-Site Scripting attacks.`,
            file: file.relativePath,
            line: lineNumber,
            code: truncateCode(lineContent),
            suggestion: 'Sanitize user input before rendering. Use DOMPurify or React\'s built-in escaping.',
            autoFixable: false,
            cweId: 'CWE-79',
            references: ['https://cwe.mitre.org/data/definitions/79.html'],
          });
        }
      }
    }
  }

  return findings;
}

// Helper functions
function getLineNumber(content: string, index: number): number {
  return content.slice(0, index).split('\n').length;
}

function truncateCode(code: string, maxLength: number = 100): string {
  if (code.length <= maxLength) return code;
  return code.slice(0, maxLength - 3) + '...';
}

function isInComment(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('//') || trimmed.startsWith('/*') || trimmed.startsWith('*');
}
